
import { useState } from 'react';
import type { Prefecture } from '../../../mocks/japanPrefectures';
import { regions } from '../../../mocks/japanPrefectures';

interface PrefectureListProps {
  prefectures: Prefecture[];
  selectedRegion: string;
  onRegionChange: (region: string) => void;
  hoveredPref: string | null;
  onHover: (id: string | null) => void;
  onSelect: (id: string) => void;
  counts?: Record<string, { artistCount: number; shopCount: number }>;
}

const REGION_COLORS: Record<string, string> = {
  'hokkaido-tohoku': '#00d9ff',
  'kanto': '#ff006e',
  'chubu': '#b537f2',
  'kinki': '#00ff41',
  'chugoku-shikoku': '#ff8c00',
  'kyushu': '#ffd600',
};

export function PrefectureList({
  prefectures,
  selectedRegion,
  onRegionChange,
  hoveredPref,
  onHover,
  onSelect,
  counts,
}: PrefectureListProps) {
  const filtered = selectedRegion === 'all'
    ? prefectures
    : prefectures.filter(p => p.region === selectedRegion);

  return (
    <div>
      {/* Region Filter */}
      <div className="flex flex-wrap gap-2 mb-6 lg:mb-8">
        {regions.map((r) => {
          const isActive = selectedRegion === r.id;
          const color = r.id === 'all' ? '#fff' : (REGION_COLORS[r.id] || '#fff');
          return (
            <button
              key={r.id}
              onClick={() => onRegionChange(r.id)}
              className={`px-3 py-1.5 rounded-full text-[10px] lg:text-xs tracking-wider font-bold transition-all duration-300 whitespace-nowrap cursor-pointer border ${
                isActive
                  ? 'bg-white/10'
                  : 'bg-transparent hover:bg-white/5'
              }`}
              style={{
                borderColor: isActive ? color : 'rgba(255,255,255,0.1)',
                color: isActive ? color : 'rgba(255,255,255,0.4)',
                boxShadow: isActive ? `0 0 12px ${color}30` : undefined,
              }}
            >
              {r.nameEn}
            </button>
          );
        })}
      </div>

      {/* Prefecture Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-3">
        {filtered.map((pref) => {
          const isHovered = hoveredPref === pref.id;
          const regionColor = REGION_COLORS[pref.region] || '#333';
          const prefCounts = counts?.[pref.id];
          const artistCount = prefCounts?.artistCount ?? pref.artistCount;
          const shopCount = prefCounts?.shopCount ?? pref.shopCount;

          return (
            <button
              key={pref.id}
              onMouseEnter={() => onHover(pref.id)}
              onMouseLeave={() => onHover(null)}
              onClick={() => onSelect(pref.id)}
              className={`relative text-left p-3 lg:p-4 rounded-xl border transition-all duration-300 cursor-pointer group overflow-hidden ${
                isHovered ? 'bg-white/5' : 'bg-white/[0.02] hover:bg-white/[0.04]'
              }`}
              style={{
                borderColor: isHovered ? `${regionColor}60` : pref.available ? `${regionColor}30` : 'rgba(255,255,255,0.05)',
                boxShadow: isHovered ? `0 0 20px ${regionColor}15` : undefined,
              }}
            >
              {/* Glow accent */}
              <div
                className="absolute top-0 right-0 w-12 h-12 opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-full blur-xl"
                style={{ background: regionColor }}
              />

              {/* Available badge */}
              {pref.available && (
                <div className="absolute top-2 right-2 flex items-center gap-1">
                  <div
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: regionColor, boxShadow: `0 0 6px ${regionColor}` }}
                  />
                  <span className="text-[8px] tracking-wider font-bold" style={{ color: regionColor }}>LIVE</span>
                </div>
              )}

              <span
                className="font-display text-sm lg:text-lg tracking-wider block transition-colors duration-300"
                style={{ color: isHovered ? '#fff' : pref.available ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.3)' }}
              >
                {pref.nameEn}
              </span>
              <span className="text-gray-600 text-[10px] block mt-0.5">{pref.name}</span>

              <div className="flex items-center gap-3 mt-2">
                <div className="flex items-center gap-1">
                  <i className="ri-user-line text-[9px]" style={{ color: regionColor, opacity: 0.6 }} />
                  <span className="text-[9px] text-gray-600">{artistCount}</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-store-2-line text-[9px]" style={{ color: regionColor, opacity: 0.6 }} />
                  <span className="text-[9px] text-gray-600">{shopCount}</span>
                </div>
              </div>

              {!pref.available && (
                <span className="text-[8px] text-gray-700 tracking-wider mt-1 block">COMING SOON</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
